import { AfterViewInit, Component, effect, inject } from '@angular/core';
import { BaseListComponent } from '../base-list/base-list.component';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { MenuItem, MessageService, SharedModule } from 'primeng/api';
import { History } from '../../../models/history';
import { HistoryStore } from '../../../stores/history/history.state';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';
import { BadgeModule } from 'primeng/badge';
import { SidebarModule } from 'primeng/sidebar';
import { DialogModule } from 'primeng/dialog';
import { AvatarModule } from 'primeng/avatar';
import { TableCaptionComponent } from '../../molecules/table-list-caption/table-list-caption.component';
import { Table, TableModule } from 'primeng/table';
import { DatePipe } from '@angular/common';
import { UserStore } from '../../../stores/user/user.state';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { User } from '../../../models/user';
import { getProfileImagePath } from '../../../utility/app.utility';
import { addDays } from 'date-fns';
import { getState } from '@ngrx/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/**
 * A `History` entry enriched with the display text/icon that is actually
 * shown in the table/dialog. Keeping these as real properties (rather than
 * computing them in the template) lets the table's global filter search the
 * translated, human-readable text instead of raw stored codes.
 */
export interface HistoryRow extends History {
  entityTypeLabel: string;
  entityTypeIcon: string;
  actionLabel: string;
  actionIcon: string;
  userLabel: string;
  resolvedUser?: User;
}

const FALLBACK_ENTITY_ICON = 'fas fa-circle-question';
const FALLBACK_ACTION_ICON = 'fas fa-clock-rotate-left';
const UNKNOWN_USER_LABEL = 'Unbekannte:r Nutzer:in';

@Component({
  selector: 'app-history-list',
  standalone: true,
  imports: [
    BadgeModule,
    SharedModule,
    SidebarModule,
    DialogModule,
    AvatarModule,
    TableCaptionComponent,
    TableModule,
    DatePipe,
    TranslatePipe,
  ],

  templateUrl: './history-list.component.html',
  styleUrl: './history-list.component.scss',
})
export class HistoryListComponent
  extends BaseListComponent<History>
  implements AfterViewInit
{
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-10',
    icon: 'fas fa-history',
    headerText: 'History',
  };

  readonly store = inject(HistoryStore);
  readonly userStore = inject(UserStore);
  readonly activeUserStore = inject(ActiveUserStore);
  override menuItems: MenuItem[] = [];

  items: HistoryRow[] = []; // Your actual data
  filteredItems: HistoryRow[] = [];

  fromDate: Date | null = null;
  toDate: Date | null = null;

  selectedItem: HistoryRow | null = null;

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private translate: TranslateService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );

    effect(() => {
      const historyState = getState(this.store);
      this.items = this.store.asArray().map(item => this.toRow(item));
      if (this.store.initialized()) {
        this.filteredItems = this.items;
      }
    });

    this.translate.setDefaultLang('en'); // Set default language
    this.translate.use('de'); // Use German for translations
  }

  /**
   * A lifecycle hook called after Angular has fully initialized a component's view.
   * This method is used to configure the sidebar with the given configuration
   * and initialize the items list from the store, as well as set up the filtered items.
   *
   * @return {void} Does not return a value.
   */
  ngAfterViewInit(): void {
    this.sidebarService.setConfiguration(this.sidebarConfig);
    this.items = this.store.asArray().map(item => this.toRow(item)); // ORDER!!
    this.filteredItems = this.items;
  }

  /**
   * Enriches a raw history entry with the translated text/icon that is
   * actually rendered, falling back to a humanized version of the stored
   * code (instead of a raw, untranslated i18n key) when no translation
   * exists - e.g. for entity types/actions from old, no longer used data.
   */
  private toRow(item: History): HistoryRow {
    const resolvedUser = item.user
      ? this.userStore.getById(item.user)
      : undefined;

    return {
      ...item,
      entityTypeLabel: this.translateOrFallback(
        `entityTypes.${item.entityType}.text`,
        item.entityType
      ),
      entityTypeIcon: this.translateOrFallback(
        `entityTypes.${item.entityType}.icon`,
        undefined,
        FALLBACK_ENTITY_ICON
      ),
      actionLabel: this.translateOrFallback(
        `actions.${item.action}.text`,
        item.action
      ),
      actionIcon: this.translateOrFallback(
        `actions.${item.action}.icon`,
        undefined,
        FALLBACK_ACTION_ICON
      ),
      userLabel: resolvedUser?.username || UNKNOWN_USER_LABEL,
      resolvedUser,
    };
  }

  /** URL of the user's uploaded profile picture, or `null` if they haven't set one. */
  getUserImagePath(user: User | undefined): string | null {
    if (!user?.image) return null;
    return getProfileImagePath(
      user.commune || this.activeUserStore.commune(),
      user.image
    );
  }

  getUserInitial(user: User | undefined): string {
    return (user?.firstName?.[0] || user?.username?.[0] || '?').toUpperCase();
  }

  /**
   * ngx-translate returns the key itself when no translation is found, which
   * is what previously showed up as raw strings like
   * "entityTypes.District.text". Detect that and fall back to something more
   * readable instead.
   */
  private translateOrFallback(
    key: string,
    rawValue?: string,
    defaultValue = ''
  ): string {
    const translated = this.translate.instant(key);
    if (translated !== key) {
      return translated;
    }
    return rawValue ? this.humanize(rawValue) : defaultValue;
  }

  private humanize(value: string): string {
    return value.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  }

  openDetail(item: HistoryRow): void {
    this.selectedItem = item;
  }

  onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.selectedItem = null;
    }
  }

  /**
   * Applies a date filter on the list of items based on `fromDate` and/or `toDate`.
   * Filters the `items` list and updates the `filteredItems` property with items
   * that have a `createdAt` timestamp within the specified date range.
   * If no `fromDate` or `toDate` is defined, all items are included in `filteredItems`.
   *
   * @return {void} Does not return a value. Updates `filteredItems` based on the applied date filter.
   */
  applyDateFilter(): void {
    console.log(this.fromDate, this.toDate);
    if (this.fromDate || this.toDate) {
      this.filteredItems = this.items.filter((item: History) => {
        if (item.createdAt) {
          const itemDate = new Date(item.createdAt * 1000);
          const isAfterFromDate = this.fromDate
            ? itemDate >= this.fromDate
            : true;
          const isBeforeToDate = this.toDate
            ? itemDate <= addDays(this.toDate, 1)
            : true;
          return isAfterFromDate && isBeforeToDate;
        }
        return false;
      });
    } else {
      this.filteredItems = this.items;
    }
  }

  /**
   * Handles the change event for the 'from date' field, updating the internal state and applying the date filter accordingly.
   *
   * @param {Date|null} date - The selected 'from date'. Can be null to indicate no date is selected.
   * @return {void} This method does not return a value.
   */
  onFromDateChange(date: Date | null): void {
    this.fromDate = date;
    this.applyDateFilter();
  }

  /**
   * Handles changes to the "toDate" property by updating its value and applying the date filter logic.
   *
   * @param {Date | null} date - The new date value to be set for the "toDate" property or null if no date is selected.
   * @return {void} This method does not return a value.
   */
  onToDateChange(date: Date | null): void {
    this.toDate = date;
    this.applyDateFilter();
  }

  /**
   * Resets the provided table and related filter parameters to their initial state.
   *
   * @param {Table} table - The table instance to be cleared.
   * @return {void} Does not return a value.
   */
  override clear(table: Table): void {
    table.clear();
    this.searchWord = '';
    this.fromDate = null;
    this.toDate = null;
    this.filteredItems = this.items;
  }
}
